import { fade, hex, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawCaptionGlyph, drawCheckmark } from "./glyphs.js";
import {
  Theme,
  type CaptionButton,
  type ScrollBarParts,
  type ThemeMetrics,
  type ThemePalette,
  type VisualState,
  type WindowVisualState,
} from "./Theme.js";

const UI_FONT = "'Segoe UI Variable Text', 'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'Cascadia Mono', Consolas, 'Courier New', monospace";

const METRICS: ThemeMetrics = {
  titleBarHeight: 34,
  windowBorder: 1,
  windowCornerRadius: 8,
  resizeGripSize: 6,
  captionButtonWidth: 44,
  windowPadding: insets(0),

  controlCornerRadius: 4,
  controlHeight: 30,
  buttonPadding: insets(14, 6),
  buttonMinWidth: 86,
  focusRingWidth: 2,
  focusRingInset: -2,

  textBoxPadding: insets(10, 5),

  checkBoxSize: 18,
  radioSize: 18,
  checkLabelGap: 8,

  splitterSize: 6,
  spinnerSize: 28,

  sliderThumbSize: 18,
  sliderTrackThickness: 4,
  spinButtonWidth: 17,

  scrollBarSize: 14,
  scrollBarMinThumb: 24,
  scrollBarArrows: false,

  listItemHeight: 26,
  listPadding: insets(3),
  headerHeight: 30,
  columnPadding: 8,

  dialogPadding: 16,
  dialogButtonGap: 8,

  font: { family: UI_FONT, size: 13 },
  captionFont: { family: UI_FONT, size: 13, weight: 600 },
  monoFont: { family: MONO_FONT, size: 12.5 },
};

const LIGHT: ThemePalette = {
  desktop: hex("#2b3a4a"),

  windowBg: hex("#f3f3f3"),
  windowBorderActive: hex("#00000033"),
  windowBorderInactive: hex("#00000020"),
  titleBarActive: hex("#f9f9f9"),
  titleBarInactive: hex("#f3f3f3"),
  titleTextActive: hex("#1b1b1b"),
  titleTextInactive: hex("#8a8a8a"),

  controlBg: hex("#fdfdfd"),
  controlBgHover: hex("#f6f6f6"),
  controlBgPressed: hex("#ededed"),
  controlBgDisabled: hex("#f5f5f5"),
  controlBorder: hex("#d0d0d0"),
  controlBorderHover: hex("#c4c4c4"),

  fieldBg: hex("#ffffff"),
  fieldBgDisabled: hex("#f5f5f5"),
  fieldBorder: hex("#cfcfcf"),
  fieldBorderFocused: hex("#0f6cbd"),

  text: hex("#1b1b1b"),
  textDisabled: hex("#a0a0a0"),
  textSubtle: hex("#5f6368"),
  buttonText: hex("#1b1b1b"),
  fieldText: hex("#1b1b1b"),
  placeholderText: hex("#5f6368"),
  headingText: hex("#1b1b1b"),

  accent: hex("#0f6cbd"),
  accentHover: hex("#115ea3"),
  accentPressed: hex("#0c3b5e"),
  accentText: hex("#ffffff"),

  selectionBg: hex("#0f6cbd"),
  selectionText: hex("#ffffff"),
  selectionInactiveBg: hex("#d6d6d6"),
  textSelectionBg: hex("#0f6cbd66"),

  scrollTrack: hex("#00000000"),
  scrollThumb: hex("#00000038"),
  scrollThumbHover: hex("#00000055"),
  scrollThumbPressed: hex("#00000077"),

  headerBg: hex("#fafafa"),
  headerBgHover: hex("#f0f0f0"),
  headerText: hex("#444444"),
  gridLine: hex("#e6e6e6"),

  focusRing: hex("#0f6cbd"),
  shadow: hex("#00000033"),

  bevelHighlight: hex("#ffffff"),
  bevelLight: hex("#e8e8e8"),
  bevelShadow: hex("#bdbdbd"),
  bevelDarkShadow: hex("#8a8a8a"),
};

const DARK: ThemePalette = {
  desktop: hex("#12161c"),

  windowBg: hex("#202020"),
  windowBorderActive: hex("#ffffff26"),
  windowBorderInactive: hex("#ffffff14"),
  titleBarActive: hex("#2b2b2b"),
  titleBarInactive: hex("#202020"),
  titleTextActive: hex("#f2f2f2"),
  titleTextInactive: hex("#8a8a8a"),

  controlBg: hex("#2d2d2d"),
  controlBgHover: hex("#353535"),
  controlBgPressed: hex("#272727"),
  controlBgDisabled: hex("#262626"),
  controlBorder: hex("#3d3d3d"),
  controlBorderHover: hex("#4a4a4a"),

  fieldBg: hex("#1c1c1c"),
  fieldBgDisabled: hex("#242424"),
  fieldBorder: hex("#3d3d3d"),
  fieldBorderFocused: hex("#4cc2ff"),

  text: hex("#f2f2f2"),
  textDisabled: hex("#6d6d6d"),
  textSubtle: hex("#a8a8a8"),
  buttonText: hex("#f2f2f2"),
  fieldText: hex("#f2f2f2"),
  placeholderText: hex("#a8a8a8"),
  headingText: hex("#f2f2f2"),

  accent: hex("#4cc2ff"),
  accentHover: hex("#77d1ff"),
  accentPressed: hex("#3aa0d6"),
  accentText: hex("#00243d"),

  selectionBg: hex("#4cc2ff"),
  selectionText: hex("#00243d"),
  selectionInactiveBg: hex("#3a3a3a"),
  textSelectionBg: hex("#4cc2ff55"),

  scrollTrack: hex("#00000000"),
  scrollThumb: hex("#ffffff33"),
  scrollThumbHover: hex("#ffffff55"),
  scrollThumbPressed: hex("#ffffff77"),

  headerBg: hex("#272727"),
  headerBgHover: hex("#303030"),
  headerText: hex("#c8c8c8"),
  gridLine: hex("#333333"),

  focusRing: hex("#4cc2ff"),
  shadow: hex("#00000066"),

  bevelHighlight: hex("#3a3a3a"),
  bevelLight: hex("#2f2f2f"),
  bevelShadow: hex("#141414"),
  bevelDarkShadow: hex("#000000"),
};

export type ModernVariant = "light" | "dark";

/**
 * A flat, rounded Fluent-style theme with light and dark palettes.
 *
 * Chrome is drawn with the SDF rounded-rect primitive: one quad per surface
 * plus a border, so a whole window frame costs a handful of instances.
 */
export class ModernTheme extends Theme {
  readonly name: string;
  readonly metrics = METRICS;
  readonly palette: ThemePalette;
  readonly isDark: boolean;

  constructor(variant: ModernVariant = "light") {
    super();
    this.isDark = variant === "dark";
    this.palette = this.isDark ? DARK : LIGHT;
    this.name = `modern-${variant}`;
  }

  // --- window chrome ----------------------------------------------------

  override windowClientRect(bounds: Rect, s: WindowVisualState): Rect {
    const b = this.frameBorder(s);
    const top = b + this.captionHeight(s);
    return {
      x: b,
      y: top,
      width: Math.max(0, bounds.width - b * 2),
      height: Math.max(0, bounds.height - top - b),
    };
  }

  override captionButtonRect(
    bounds: Rect,
    button: CaptionButton,
    s: WindowVisualState,
  ): Rect | null {
    const b = this.frameBorder(s);
    const h = this.captionHeight(s);
    const w = this.metrics.captionButtonWidth;
    const order: CaptionButton[] = [];
    if (s.showMinimize) order.push("minimize");
    if (s.showMaximize) order.push("maximize");
    if (s.showClose) order.push("close");
    const index = order.indexOf(button);
    if (index < 0) return null;
    // Laid out right-to-left from the frame's right edge.
    const fromRight = order.length - 1 - index;
    return { x: bounds.width - b - (fromRight + 1) * w, y: b, width: w, height: h };
  }

  override drawWindowFrame(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const p = this.palette;
    const m = this.metrics;
    const radius = s.maximized ? 0 : m.windowCornerRadius;
    const r: Rect = { x: 0, y: 0, width: bounds.width, height: bounds.height };

    if (s.active && !s.maximized) {
      // Cheap drop shadow: two offset, blurred-looking rounded rects.
      rc.fillRect({ x: 2, y: 4, width: r.width - 4, height: r.height - 2 }, fade(p.shadow, 0.5), radius);
      rc.fillRect({ x: 5, y: 8, width: r.width - 10, height: r.height - 6 }, fade(p.shadow, 0.4), radius);
    }

    rc.drawRect(r, {
      fill: p.windowBg,
      radius,
      borderWidth: this.frameBorder(s),
      borderColor: s.active ? p.windowBorderActive : p.windowBorderInactive,
    });

    // Title bar: same rounding on top, square where it meets the client area.
    const b = this.frameBorder(s);
    const titleRect: Rect = { x: b, y: b, width: r.width - b * 2, height: this.captionHeight(s) };
    rc.fillRect(titleRect, s.active ? p.titleBarActive : p.titleBarInactive, radius);
    rc.fillRect(
      { x: titleRect.x, y: titleRect.y + radius, width: titleRect.width, height: titleRect.height - radius },
      s.active ? p.titleBarActive : p.titleBarInactive,
    );

    this.#drawCaptionButtons(rc, bounds, s);
  }

  #drawCaptionButtons(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const p = this.palette;
    const buttons: CaptionButton[] = ["minimize", "maximize", "close"];
    for (const button of buttons) {
      const r = this.captionButtonRect(bounds, button, s);
      if (!r) continue;
      const hovered = s.hoveredButton === button;
      const pressed = s.pressedButton === button;
      if (hovered || pressed) {
        const bg =
          button === "close"
            ? pressed
              ? hex("#c42b1c")
              : hex("#e81123")
            : pressed
              ? p.controlBgPressed
              : p.controlBgHover;
        rc.fillRect(r, bg);
      }
      const ink =
        button === "close" && (hovered || pressed)
          ? hex("#ffffff")
          : s.active
            ? p.titleTextActive
            : p.titleTextInactive;
      drawCaptionGlyph(rc, r, button, ink, s.maximized, this.palette.titleBarActive);
    }
  }

  // --- controls ---------------------------------------------------------

  override drawButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const accent = s.isDefault === true;
    let fill: Color;
    if (s.disabled) fill = accent ? fade(p.accent, 0.4) : p.controlBgDisabled;
    else if (s.pressed) fill = accent ? p.accentPressed : p.controlBgPressed;
    else if (s.hovered) fill = accent ? p.accentHover : p.controlBgHover;
    else fill = accent ? p.accent : p.controlBg;

    rc.drawRect(r, {
      fill,
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: accent ? fade(p.accent, 0.6) : s.hovered ? p.controlBorderHover : p.controlBorder,
    });
    if (s.focused && !s.disabled) this.drawFocusRing(rc, r);
  }

  override buttonTextColor(s: VisualState): Color {
    const p = this.palette;
    if (s.disabled) return p.textDisabled;
    return s.isDefault === true ? p.accentText : p.buttonText;
  }

  override drawTextBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: s.disabled
        ? p.controlBorder
        : s.focused
          ? p.fieldBorderFocused
          : s.hovered
            ? p.controlBorderHover
            : p.fieldBorder,
    });
    // Fluent's thicker accent underline on the focused edge.
    if (s.focused && !s.disabled) {
      rc.fillRect({ x: r.x + 1, y: r.y + r.height - 2, width: r.width - 2, height: 2 }, p.fieldBorderFocused);
    }
  }

  override drawCheckBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const on = s.checked === true || s.indeterminate === true;
    let fill: Color;
    if (s.disabled) fill = on ? fade(p.accent, 0.4) : p.controlBgDisabled;
    else if (on) fill = s.pressed ? p.accentPressed : s.hovered ? p.accentHover : p.accent;
    else fill = s.pressed ? p.controlBgPressed : s.hovered ? p.controlBgHover : p.fieldBg;

    rc.drawRect(r, {
      fill,
      radius: 3,
      borderWidth: 1,
      borderColor: on ? fill : s.hovered ? p.controlBorderHover : p.controlBorder,
    });

    const ink = s.disabled ? p.textDisabled : p.accentText;
    if (s.indeterminate) {
      rc.fillRect(
        { x: r.x + r.width * 0.25, y: r.y + r.height / 2 - 1, width: r.width * 0.5, height: 2 },
        ink,
      );
    } else if (s.checked) {
      drawCheckmark(rc, r, ink);
    }
  }

  override drawRadioButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const radius = Math.min(r.width, r.height) / 2;
    const on = s.checked === true;
    const fill = s.disabled
      ? p.controlBgDisabled
      : on
        ? p.fieldBg
        : s.pressed
          ? p.controlBgPressed
          : s.hovered
            ? p.controlBgHover
            : p.fieldBg;
    const border = s.disabled
      ? p.controlBorder
      : on
        ? s.hovered
          ? p.accentHover
          : p.accent
        : s.hovered
          ? p.controlBorderHover
          : p.controlBorder;
    rc.drawRect(r, { fill, radius, borderWidth: on ? 1 : 1, borderColor: border });
    if (on) {
      const dot = Math.max(4, Math.round(r.width * 0.44));
      const inset = (r.width - dot) / 2;
      rc.fillRect(
        { x: r.x + inset, y: r.y + inset, width: dot, height: dot },
        s.disabled ? p.textDisabled : p.accent,
        dot / 2,
      );
    }
  }

  override drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: sunken ? p.fieldBg : p.windowBg,
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });
  }

  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    const p = this.palette;
    if (p.scrollTrack.a > 0) rc.fillRect(parts.bounds, p.scrollTrack);
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;

    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const pressed = parts.pressedPart === "thumb";
    const color = pressed ? p.scrollThumbPressed : hovered ? p.scrollThumbHover : p.scrollThumb;

    // The thumb slims down when idle, the way Fluent scrollbars do.
    const slim = hovered || pressed ? 0 : 3;
    const t = parts.thumb;
    const r: Rect = parts.vertical
      ? { x: t.x + slim, y: t.y, width: Math.max(2, t.width - slim * 2), height: t.height }
      : { x: t.x, y: t.y + slim, width: t.width, height: Math.max(2, t.height - slim * 2) };
    rc.fillRect(r, color, Math.min(r.width, r.height) / 2);
  }

  override drawListBackground(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: s.focused ? p.fieldBorderFocused : p.fieldBorder,
    });
  }

  override drawListItem(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    if (s.selected) {
      rc.fillRect(r, s.active === false ? p.selectionInactiveBg : p.selectionBg, 3);
    } else if (s.hovered) {
      rc.fillRect(r, p.controlBgHover, 3);
    }
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? p.controlBgPressed : s.hovered ? p.headerBgHover : p.headerBg);
    rc.vLine(r.x + r.width - 1, r.y + 5, r.height - 10, p.gridLine);
    rc.hLine(r.x, r.y + r.height - 1, r.width, p.gridLine);
  }

  override drawFocusRing(rc: RenderContext, r: Rect): void {
    const m = this.metrics;
    const i = m.focusRingInset;
    rc.strokeRect(
      { x: r.x + i, y: r.y + i, width: r.width - i * 2, height: r.height - i * 2 },
      this.palette.focusRing,
      m.focusRingWidth,
      m.controlCornerRadius + Math.abs(i),
    );
  }
}

/** Re-exported for compatibility; the implementation lives in glyphs.ts. */
export { drawCheckmark };
